"use client";

import { useEffect, useRef } from "react";
import Link from "next/link";

interface OfferCard {
    icon: React.ReactNode;
    title: string;
    body: string;
    href: string;
    linkLabel: string;
}

function IconShip() {
    return (
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="var(--color-white)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M2 21c.6.5 1.2 1 2.5 1 2.5 0 2.5-2 5-2s2.5 2 5 2 2.5-2 5-2c1.3 0 1.9.5 2.5 1" />
            <path d="M19.38 20A11.6 11.6 0 0 0 21 14l-9-4-9 4c0 2.9.94 5.34 2.81 7.76" />
            <path d="M19 13V7a2 2 0 0 0-2-2H7a2 2 0 0 0-2 2v6" />
            <path d="M12 10v4" />
            <path d="M8 10v4M16 10v4" />
        </svg>
    );
}

function IconContract() {
    return (
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="var(--color-white)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
            <polyline points="14 2 14 8 20 8" />
            <line x1="16" y1="13" x2="8" y2="13" />
            <line x1="16" y1="17" x2="8" y2="17" />
            <polyline points="10 9 9 9 8 9" />
        </svg>
    );
}

function IconShield() {
    return (
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="var(--color-white)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
        </svg>
    );
}

function IconChart() {
    return (
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="var(--color-white)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <line x1="18" y1="20" x2="18" y2="10" />
            <line x1="12" y1="20" x2="12" y2="4" />
            <line x1="6" y1="20" x2="6" y2="14" />
        </svg>
    );
}

const offers: OfferCard[] = [
    {
        icon: <IconContract />,
        title: "Long-Term Offtake",
        body: "Structured 1–5 year offtake agreements with defined volume bands, grade specifications, and price escalation mechanisms.",
        href: "/commercial",
        linkLabel: "View Terms",
    },
    {
        icon: <IconShip />,
        title: "Logistics & Delivery",
        body: "CIF, CFR, and DAP delivery to major port terminals. Full documentation package included with every shipment.",
        href: "/operations",
        linkLabel: "View Logistics",
    },
    {
        icon: <IconChart />,
        title: "Supply Planning",
        body: "Quarterly supply forecasts aligned to your production schedule. Dedicated account manager for scheduling coordination.",
        href: "/operations",
        linkLabel: "View Operations",
    },
    {
        icon: <IconShield />,
        title: "ESG & Compliance",
        body: "Chain-of-custody documentation, OECD-aligned due diligence, and export licence management included as standard.",
        href: "/homepage",
        linkLabel: "Learn More",
    },
];

export default function WhatWeOfferSection() {
    const ref = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const els = ref.current?.querySelectorAll(".reveal");
        if (!els) return;
        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((e) => {
                    if (e.isIntersecting) {
                        e.target.classList.add("visible");
                        observer.unobserve(e.target);
                    }
                });
            },
            { threshold: 0.1 }
        );
        els.forEach((el) => observer.observe(el));
        return () => observer.disconnect();
    }, []);

    return (
        <section
            ref={ref}
            className="section-navy-mid"
            style={{ padding: "120px 0" }}
            aria-labelledby="offer-heading"
        >
            <div
                className="px-6 lg:px-12"
                style={{ maxWidth: "1280px", margin: "0 auto" }}
            >
                {/* Header */}
                <div
                    className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-16"
                    style={{ borderBottom: "1px solid var(--color-border)", paddingBottom: "48px" }}
                >
                    <div>
                        <span className="type-subheading reveal">What We Offer</span>
                        <h2
                            id="offer-heading"
                            className="type-display-dark reveal reveal-delay-1"
                            style={{ marginTop: "12px", maxWidth: "440px" }}
                        >
                            End-to-end supply
                            <br />
                            from mine to refinery.
                        </h2>
                    </div>
                    <p
                        className="type-body reveal reveal-delay-2"
                        style={{ maxWidth: "380px" }}
                    >
                        Every service is structured for the commercial and operational
                        requirements of industrial-scale buyers. No intermediary markup,
                        no ambiguity.
                    </p>
                </div>

                {/* Cards Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                    {offers.map((card, i) => (
                        <div
                            key={card.title}
                            className={`card reveal reveal-delay-${i + 1} flex flex-col`}
                            style={{ padding: "32px", gap: "20px" }}
                        >
                            <div className="icon-box">{card.icon}</div>
                            <h3
                                style={{
                                    fontFamily: "var(--font-serif)",
                                    fontSize: "1.25rem",
                                    fontWeight: 400,
                                    color: "var(--color-white)",
                                    lineHeight: "1.2",
                                }}
                            >
                                {card.title}
                            </h3>
                            <p
                                className="type-body"
                                style={{ flex: 1, fontSize: "0.875rem" , color: "rgba(255,255,255,0.38)"}}
                            >
                                {card.body}
                            </p>
                            <Link
                                href={card.href}
                                style={{
                                    fontFamily: "var(--font-sans)",
                                    fontSize: "0.75rem",
                                    fontWeight: 600,
                                    letterSpacing: "0.08em",
                                    textTransform: "uppercase",
                                    color: "var(--color-white)",
                                    textDecoration: "none",
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "6px",
                                    transition: "gap 150ms",
                                }}
                                onMouseEnter={(e) => {
                                    const el = e.currentTarget;
                                    el.style.gap = "10px";
                                }}
                                onMouseLeave={(e) => {
                                    const el = e.currentTarget;
                                    el.style.gap = "6px";
                                }}
                            >
                                {card.linkLabel}
                                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                    <path d="M5 12h14M12 5l7 7-7 7" />
                                </svg>
                            </Link>
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
}